import { h, render, type ComponentChild } from "preact";
import { dispatch } from "./messageBus";
import { ToastAction, TOAST } from "./messageBus/actions";
import { Button, Container, Dialog, DialogStandardContent } from "uitoolkit";

export function uiToast(message: string, type?: ToastAction["payload"]["type"], undo?: () => void): void {
    // todo: refactor away from the messages and do something similar to the modal manager
    dispatch({
        type: TOAST,
        payload: {
            message,
            type,
            undo
        }
    })
}

export function uiAlert(content: ComponentChild, opt?: { title?: string, closeOnBackdrop?: boolean }): Promise<void> {
    const ui = (
        <DialogStandardContent
            header={
                opt?.title &&
                <h1>{opt?.title}</h1>
            }
            footer={
                <Container class="row gap-8 justify-end">
                    <Button variant="primary" onClick={() => uiCloseModal()}>Ok</Button>
                </Container>
            }>
            <p>{content}</p>
        </DialogStandardContent>
    );
    return uiShowModal({
        ui,
        closeOnBackdrop: opt?.closeOnBackdrop ?? true,
    }).promise
}

export function uiConfirm(content: ComponentChild, opt?: { title?: string, closeOnBackdrop?: boolean }): Promise<boolean> {
    const ui = (
        <DialogStandardContent
            header={
                opt?.title &&
                <h1>{opt?.title}</h1>
            }
            footer={
                <Container class="row gap-8 justify-end">
                    <Button variant="default" onClick={() => uiCloseModal({ returnValue: false })}>Cancel</Button>
                    <Button variant="primary" onClick={() => uiCloseModal({ returnValue: true })}>Ok</Button>
                </Container>
            }>
            <p>{content}</p>
        </DialogStandardContent>
    );
    return uiShowModal<boolean>({
        ui,
        closeOnBackdrop: opt?.closeOnBackdrop ?? false,
        defaultValue: false
    }).promise
}

type InternalModalState = {
    id: string;
    container: HTMLDivElement,
    isClosed: boolean,
    returnValue?: unknown,
    resolve?: (value: unknown | PromiseLike<unknown>) => void,
    reject?: (reason?: any) => void,
    onClose?: () => void,
};
const allModalStates: InternalModalState[] = [];

export function uiShowModal<T = void>(opt: {
    ui: ComponentChild,
    classes?: string,
    id?: string,
    defaultValue?: T,
    onClose?: () => void,
    closeOnBackdrop?: boolean,
}): {
    id: string,
    promise: Promise<T>,
    closeModal: (status?: "resolve" | "reject") => void,
    closeModalWithValue: (returnValue: T) => void,
} {
    let container = document.querySelector("#modalcontainer");
    if (!container) {
        container = document.createElement("div");
        container.id = "modalcontainer";
        document.body.appendChild(container)
    }
    const internalState: InternalModalState = {
        id: opt.id ?? `modal${allModalStates.length}`,
        container: document.createElement("div"),
        returnValue: opt.defaultValue,
        isClosed: false,
        onClose: opt.onClose
    };
    allModalStates.push(internalState);
    container.appendChild(internalState.container);

    const closeModal = (status: "resolve" | "reject" = "resolve") => _closeModal<T>(internalState);
    const closeModalWithValue = (returnValue: T) => {
        _closeModal<T>(internalState);
    };

    const promise = new Promise<T>((resolve, reject) => {
        internalState.resolve = resolve as (value: unknown | PromiseLike<unknown>) => void;
        internalState.reject = reject;
        const ui = (
            <Dialog
                closeOnBackdrop={opt.closeOnBackdrop ?? true}
                open={true}
                className={opt.classes === undefined ? "centered" : opt.classes}
                onClose={closeModal}>
                {opt.ui}
            </Dialog>
        );
        render(ui, internalState.container);
    });

    return {
        id: internalState.id,
        promise: promise as Promise<T>,
        closeModal,
        closeModalWithValue
    }
}

function _closeModal<T = void>(state: InternalModalState, status: "resolve" | "reject" = "resolve") {
    // todo: consider returning a promise that resolves after any animations have played

    if (!state.isClosed) {
        state.isClosed = true;

        render(null, state.container); // unmount
        state.container.remove();
        state.onClose?.();

        if (status == "resolve")
            state.resolve?.(state.returnValue as T);
        else
            state.reject?.();

        const index = allModalStates.indexOf(state);
        if (index != -1) {
            allModalStates.splice(index, 1);
        }
    }
}

export function uiCloseModal(opt?: { id?: string, returnValue?: unknown }): void {
    // todo: consider returning a promise that resolves after any animations have played

    // close the top modal if no id is specified
    if (opt?.id === undefined) {
        if (allModalStates.length > 0) {
            const state = allModalStates.pop()!;
            if (!!opt && 'returnValue' in opt) {
                state.returnValue = opt.returnValue;
            }
            _closeModal(state);
        }
        return;
    }

    const index = allModalStates.findIndex(n => n.id == opt.id);
    if (index == -1) return;
    const state = allModalStates[index];
    if ('returnValue' in opt) {
        state.returnValue = opt.returnValue;
    }
    _closeModal(allModalStates[index]);
}

export function uiCloseAllModals(): void {
    for (let i = allModalStates.length - 1; i >= 0; i--) {
        _closeModal(allModalStates[i]);
    }
}
